import { managesDevicesAcrossCompanies } from 'src/app/models/user.model';
import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AutocompleteComponent } from 'src/app/components/atuocomplete/autocomplete.component';
import { ButtonComponent } from 'src/app/components/button/button.component';
import {
  DeviceAssociationRequest,
  DeviceCertificateApprovalBody,
  DeviceCertificateInfo,
} from 'src/app/models/certificate.models';
import { CompanyNameType } from 'src/app/models/company.model';
import { AlertService } from 'src/app/services/alert.service';
import { CertificateService } from 'src/app/services/certificate.service';
import { CertificateStore } from 'src/app/services/certificate.store';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';

@Component({
  selector: 'app-device-registration',
  imports: [
    CommonModule,
    TranslateModule,
    MatInputModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    AutocompleteComponent,
    ButtonComponent,
    RouterLink,
  ],
  templateUrl: './device-registration.component.html',
  styleUrl: './device-registration.component.scss',
})
export class DeviceRegistrationComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute, { optional: true });
  private store = inject(Store);
  private router = inject(Router);
  private profile = toSignal(this.store.select(selectProfileState), {
    requireSync: false,
  });
  errorMessage: string = '';
  certificatePin: string = '';
  showValidationErrors: boolean = false;
  certificateService = inject(CertificateService);
  certificateStore = inject(CertificateStore);
  alertService = inject(AlertService);
  certificateInfo: DeviceCertificateInfo | null = null;
  searchHasAttempted: boolean = false;
  companies: CompanyNameType[] =
    this.activatedRoute?.snapshot.data['companies'] ?? [];
  selectedCompany: CompanyNameType | null = null;
  associationRequest: DeviceAssociationRequest = {};
  translateService = inject(TranslateService);

  constructor() {
    effect(() => {
      const profile = this.profile();
      if (!profile || managesDevicesAcrossCompanies(profile)) {
        // They register for companies other than their own, so nothing is preselected.
        return;
      }

      const userCompany =
        this.findCompanyById(profile.companyId) ??
        this.getSingleCompanyOption();
      this.selectedCompany = userCompany ?? null;
      this.associationRequest = {
        ...this.associationRequest,
        companyId: userCompany?.id ?? profile.companyId ?? '',
      };
    });
  }

  ngOnInit(): void {
    const deepLinkPin = this.getDeepLinkPin();
    if (!deepLinkPin) {
      return;
    }

    this.certificatePin = deepLinkPin;
    this.getInfoFromPin();
  }

  get creatorEmail(): string {
    return this.certificateInfo?.creator?.email ?? '';
  }

  get searchResults(): DeviceCertificateInfo[] {
    return this.certificateStore.searchResults();
  }

  get showNoSearchResults(): boolean {
    return (
      this.searchHasAttempted &&
      !this.certificateStore.loading() &&
      this.searchResults.length === 0
    );
  }

  get currentIp(): string {
    return this.certificateInfo?.currentIp ?? '';
  }

  get deviceIp(): string {
    return this.certificateInfo?.deviceIp ?? '';
  }

  get createdAt(): string {
    const timestamp = this.certificateInfo?.createdAt;
    if (!timestamp) {
      return '';
    }

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp;
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  }

  get isCompanyIdMissing(): boolean {
    return !this.associationRequest.companyId?.trim();
  }

  get canApprove(): boolean {
    return (
      !!this.certificateInfo &&
      !!this.certificatePin.trim() &&
      !this.isCompanyIdMissing
    );
  }

  get showCertificateForm(): boolean {
    return !!this.certificateInfo;
  }

  get hasExistingRegisteredDevice(): boolean {
    return !!this.certificateInfo?.existingDevice;
  }

  /** The batches the existing device is licensed from, which confirming it elsewhere would release. */
  get existingDeviceLicenseBatches(): Array<{ id: string; name: string }> {
    return (this.certificateInfo?.existingDevice?.licenseBatches ?? [])
      .map((batch) => ({
        id: batch.id?.trim() ?? '',
        name: batch.name?.trim() || batch.id?.trim() || '',
      }))
      .filter((batch) => !!batch.name);
  }

  get existingDeviceLicenseNames(): string {
    return this.existingDeviceLicenseBatches
      .map((batch) => batch.name)
      .join(', ');
  }

  private get existingDeviceCompanyId(): string {
    return this.certificateInfo?.existingDevice?.company?.id?.trim() ?? '';
  }

  private get isChangingExistingDeviceCompany(): boolean {
    return (
      !this.isCompanyIdMissing &&
      this.existingDeviceCompanyId !== this.associationRequest.companyId?.trim()
    );
  }

  /** The device leaves the company it is in for the one selected. */
  get willMoveExistingDevice(): boolean {
    return (
      !!this.existingDeviceCompanyId && this.isChangingExistingDeviceCompany
    );
  }

  /** Licenses are released only when the device ends up in a different company than it is in now. */
  get willReleaseExistingDeviceLicenses(): boolean {
    return (
      this.existingDeviceLicenseBatches.length > 0 &&
      this.isChangingExistingDeviceCompany
    );
  }

  get existingDeviceId(): string {
    return this.certificateInfo?.existingDevice?.deviceId?.trim() ?? '';
  }

  get selectedCompanyName(): string {
    return (
      this.selectedCompany?.name?.trim() ||
      this.associationRequest.companyId?.trim() ||
      '-'
    );
  }

  get existingDeviceName(): string {
    return (
      this.certificateInfo?.existingDevice?.nameAlias?.trim() ||
      this.certificateInfo?.existingDevice?.deviceSummary?.defaultName?.trim() ||
      this.certificateInfo?.existingDevice?.deviceId?.trim() ||
      this.certificateInfo?.deviceId?.trim() ||
      '-'
    );
  }

  get existingDeviceCompany(): string {
    return (
      this.certificateInfo?.existingDevice?.company?.name?.trim() ||
      this.certificateInfo?.existingDevice?.company?.id?.trim() ||
      '-'
    );
  }

  get hasCertificatePinInput(): boolean {
    return !!this.certificatePin.trim();
  }

  get isSuperAdmin(): boolean {
    return managesDevicesAcrossCompanies(this.profile());
  }

  get companyPreSelectedValue(): CompanyNameType | string | null {
    if (this.selectedCompany) {
      return this.selectedCompany;
    }
    return this.associationRequest.companyId?.trim() || null;
  }

  getInfoFromPin() {
    const pin = this.certificatePin.trim();
    if (!pin) {
      return;
    }

    this.certificatePin = pin;
    this.certificateStore.clearSearchResults();
    this.searchHasAttempted = false;
    // Call service to get info
    this.certificateService.getDeviceCertificateInfoByPasscode(pin).subscribe({
      next: (info) => {
        const existingDevice = info.existingDevice;
        const existingName =
          existingDevice?.nameAlias?.trim() ||
          existingDevice?.deviceSummary?.defaultName?.trim();
        const companyId = info.company?.id?.trim();
        const preselectedCompany = this.findCompanyById(companyId);

        this.certificateInfo = info;
        this.selectedCompany = preselectedCompany ?? null;
        this.associationRequest = {
          deviceIdOverride: info.deviceId ?? '',
          deviceTypeOverride: info.deviceType ?? '',
          companyId: preselectedCompany?.id ?? companyId ?? '',
          nameAlias: existingName || this.associationRequest.nameAlias || '',
          note: this.associationRequest.note ?? '',
        };
        this.showValidationErrors = false;
        this.errorMessage = '';
      },
      error: (err) => {
        this.errorMessage = this.translateService.instant(
          'certificate.pin_info.error',
        );
      },
    });
  }

  searchAssociations() {
    if (!this.isSuperAdmin) {
      return;
    }

    const searchValue = this.certificatePin.trim();
    if (!searchValue) {
      return;
    }

    this.errorMessage = '';
    this.searchHasAttempted = true;
    this.certificateStore.searchAssociationByDeviceIdSubstring(searchValue);
  }

  selectAssociation(result: DeviceCertificateInfo) {
    const lookupValue =
      result.confirmationPin?.trim() ?? result.deviceId?.trim() ?? '';
    if (!lookupValue) {
      this.alertService.error(
        this.translateService.instant('certificate.pin_info.error'),
      );
      return;
    }

    this.certificatePin = lookupValue;
    this.getInfoFromPin();
  }

  formatTimestamp(timestamp?: string): string {
    if (!timestamp) {
      return '-';
    }
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  }

  trackBySearchResult(index: number, result: DeviceCertificateInfo): string {
    return (
      result.confirmationPin ??
      result.deviceId ??
      `${result.deviceType ?? 'association'}-${index}`
    );
  }

  approveCertificate() {
    if (!this.certificateInfo) {
      return;
    }
    if (!this.canApprove) {
      this.showValidationErrors = true;
      this.alertService.error(
        this.translateService.instant('certificate.required_fields.error'),
      );
      return;
    }

    const companyId = this.associationRequest.companyId!.trim();
    const nameAlias = this.associationRequest.nameAlias?.trim();

    const body: DeviceCertificateApprovalBody = {
      // Keep overrides aligned with the fetched device details.
      deviceIdOverride: this.certificateInfo.deviceId,
      deviceTypeOverride: this.certificateInfo.deviceType,
      companyId,
      ...(nameAlias ? { nameAlias } : {}),
      note: this.associationRequest.note?.trim() || undefined,
    };

    this.certificateService
      .validateDeviceCertificate(this.certificatePin, body)
      .subscribe({
        next: () => {
          this.alertService.success(
            this.translateService.instant('certificate.approval.success'),
          );
          this.cancel();
          this.router.navigateByUrl('/resources');
        },
        error: (err) => {
          this.alertService.error(
            this.translateService.instant('certificate.approval.error'),
          );
        },
      });
  }

  cancel() {
    this.certificateInfo = null;
    this.certificatePin = '';
    this.associationRequest = {};
    this.selectedCompany = null;
    this.showValidationErrors = false;
    this.errorMessage = '';
    this.searchHasAttempted = false;
    this.certificateStore.clearSearchResults();
  }

  onCompanySelected(selected: CompanyNameType | string) {
    if (!this.isSuperAdmin) {
      return;
    }

    let company: CompanyNameType | undefined;
    if (typeof selected === 'string') {
      company = this.findCompanyByNameOrId(selected);
    } else {
      company = selected;
    }
    this.selectedCompany = company ?? null;
    this.associationRequest = {
      ...this.associationRequest,
      companyId: this.selectedCompany?.id ?? '',
    };
  }

  companyFormatter = (item: CompanyNameType | null, value: string): string => {
    if (!item) {
      return value;
    }
    return `${item.name}`;
  };

  private getSingleCompanyOption(): CompanyNameType | undefined {
    if (this.companies.length !== 1) {
      return undefined;
    }
    return this.companies[0];
  }

  private findCompanyById(companyId?: string): CompanyNameType | undefined {
    const normalized = companyId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.companies.find((x) => x.id.toLowerCase() === normalized);
  }

  private findCompanyByNameOrId(value?: string): CompanyNameType | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId =
      this.extractCompanyIdFromFormattedValue(raw)?.toLowerCase();
    return this.companies.find(
      (x) =>
        x.id.toLowerCase() === normalized ||
        x.name.toLowerCase() === normalized ||
        `${x.name} (${x.id})`.toLowerCase() === normalized ||
        (!!parsedId && x.id.toLowerCase() === parsedId),
    );
  }

  private extractCompanyIdFromFormattedValue(
    value: string,
  ): string | undefined {
    const match = value.match(/\(([^()]+)\)\s*$/);
    return match?.[1]?.trim();
  }

  private getDeepLinkPin(): string {
    const paramMap = this.activatedRoute?.snapshot.paramMap;
    return paramMap?.get('code')?.trim() || '';
  }
}
